import { QRCode } from "@pandas/components";
import { useTranslation } from "react-i18next";
import LoadingDots from "@/components/LoadingDots";

import { servicesOrchestratorValues } from "@/config/services";
import { useStore } from "@/store";
import useAssessmentFlowUrl from "./useAssessmentFlowUrl";
import { useDeviceType } from "./useDeviceType";
import useUrlShortener from "./useUrlShortener";

export class PeerConnectionError extends Error {
	constructor(message: string = "Peer disconnected") {
		super(message);
	}
}

const issues = {
	"screen-on": {
		description: "popup.The evaluated device's screen is turned on.",
	},
	"active-window": {
		description: "popup.The evaluated device is on the browser window where the evaluation takes place.",
	},
	"no-low-power-mode": {
		description: "popup.Both devices are not on low-power mode.",
	},
	"stable-network": {
		description: "popup.Your internet connection is stable.",
	},
};

export const usePeerConnectionErrors = () => {
	const { t } = useTranslation();
	const setError = useStore((state) => state.setError);
	const { isScannerDevice } = useDeviceType();
	const { shortUrl, getShortUrl } = useUrlShortener();

	const assessmentFlowUrl = useAssessmentFlowUrl();

	const peerConnectionErrorPopupParams = (error: PeerConnectionError) => {
		if (error.message === "SCAN_QR_TO_PAIR")
			return {
				icon: (
					<QRCode
						id="qr-code"
						size={100}
						className="bg-white rounded-lg p-4 sm:p-8 mx-auto w-[180px] h-[180px] sm:w-[300px] sm:h-[300px]"
						value={assessmentFlowUrl}
					/>
				),
				title: t("popup.Scan QR to pair the devices again"),
				description: t("popup.Use the evaluated device’s camera to scan the QR Code and continue where you left off."),
				body: (
					<div className="flex flex-col justify-center items-center h-full bg-lightgrey p-4 rounded-lg mt-6">
						<h6 className="my-2">{t("popup.You can also enter the URL manually on the evaluated device.")}</h6>
						<h4 className="font-semibold">{shortUrl ? shortUrl : <LoadingDots />}</h4>
					</div>
				),
				closeButton: () => setError(null),
			};
		return {
			icon: (
				<div className="w-64 h-16 relative mb-2 shrink-0">
					<img
						src={`${servicesOrchestratorValues.urls.cdn}/shared/peer-connection-errors/unpaired-devices.svg`}
						alt="peer-connection-error-icon"
						className="w-full h-full object-contain"
					/>
				</div>
			),
			title: t("popup.Oops, devices got unpaired!"),
			description: t("popup.Ensure the following to continue"),
			closeButton: () => setError(null),
			body: (
				<div className="max-h-[50vh] overflow-scroll max-w-[90%] w-[min(700px,100%)] mt-4 sm:mt-6">
					<div className="flex flex-col gap-2 w-fit">
						{Object.entries(issues).map(([key, { description }]) => {
							return (
								<div
									key={key}
									className="rounded-md bg-lightgrey flex items-center p-4 sm:p-8 gap-4 sm:gap-8"
								>
									<div className="w-10 h-10 md:w-16 md:h-16 relative shrink-0">
										<img
											src={`${servicesOrchestratorValues.urls.cdn}/shared/peer-connection-errors/${key}.svg`}
											alt="peer-connection-error-icon"
											className="w-full h-full object-contain"
										/>
									</div>
									<div className="flex flex-col text-left">
										<p>{t(description)}</p>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			),
			secondaryButtonLabel: isScannerDevice ? t("btn.I can’t find the browser window") : undefined,
			secondaryButtonOnClick: isScannerDevice
				? () => {
						getShortUrl(assessmentFlowUrl);
						setError(new PeerConnectionError("SCAN_QR_TO_PAIR"));
					}
				: undefined,
		};
	};

	return {
		peerConnectionErrorPopupParams,
	};
};
